import { Injectable } from '@angular/core';
import {
  MarketColumn,
  MarketColumnDetail,
  MarketDashboard,
  MarketGroup,
  MarketWidget,
} from '@clean-code/models';
import { cloneObject, DateParserService } from '@clean-code/shared/common';
import { NormalizedMarketDashboardResponse } from '../models/market-dashboard-response';

@Injectable({ providedIn: 'root' })
export class MarketDashboardNormalizerService {
  public normalize(value: MarketDashboard): NormalizedMarketDashboardResponse {
    const dashboard = cloneObject(value);
    const loadedColumns = dashboard.cols;
    const columns: MarketColumn[] = [];
    const detailColumns: NormalizedMarketDashboardResponse['detailColumns'] =
      [];
    const groups: NormalizedMarketDashboardResponse['groups'] = [];
    const widgets: MarketWidget[] = [];

    delete dashboard.cols;

    loadedColumns.forEach((loadedColumn) => {
      const loadedSummaries = loadedColumn.summaries;
      const loadedDetails = loadedColumn.details;

      columns.push(this.normalizeColumnData(loadedColumn));

      loadedDetails.forEach((loadedDetailColumn) => {
        const loadedGroups = loadedDetailColumn.items;

        detailColumns.push(this.shapeDetailColumn(loadedDetailColumn));

        loadedGroups.forEach((loadedGroup) => {
          this.normalizeGroupData(loadedGroup, groups, widgets);
        });
      });

      loadedSummaries.forEach((loadedGroup) => {
        this.normalizeGroupData(loadedGroup, groups, widgets);
      });
    });

    return {
      dashboard,
      columns,
      detailColumns,
      groups,
      widgets,
    };
  }

  public normalizeColumn(
    column: MarketColumn,
  ): NormalizedMarketDashboardResponse['columns'][number] {
    return this.normalizeColumnData(cloneObject(column), true);
  }

  public normalizeGroup(
    group: MarketGroup,
  ): NormalizedMarketDashboardResponse['groups'][number] {
    return this.shapeGroup(cloneObject(group));
  }

  public normalizeDetailColumn(
    detailColumn: MarketColumnDetail,
  ): NormalizedMarketDashboardResponse['detailColumns'][number] {
    return this.shapeDetailColumn(cloneObject(detailColumn));
  }

  private normalizeColumnData(
    column: MarketColumn,
    omitUndefinedExtended = false,
  ): NormalizedMarketDashboardResponse['columns'][number] {
    const hasDetails = column.details?.length > 0;

    delete column.summaries;
    delete column.details;

    column.hasDetails = hasDetails;

    if (omitUndefinedExtended && column.extended === undefined) {
      delete column.extended;
    }

    return column;
  }

  private normalizeGroupData(
    group: MarketGroup,
    groups: NormalizedMarketDashboardResponse['groups'],
    widgets: MarketWidget[],
  ): void {
    const loadedWidgets = group.items;

    groups.push(this.shapeGroup(group));
    widgets.push(
      ...loadedWidgets.map((widget) => this.normalizeWidgetData(widget)),
    );
  }

  private shapeDetailColumn(
    detailColumn: MarketColumnDetail,
  ): NormalizedMarketDashboardResponse['detailColumns'][number] {
    delete detailColumn.items;

    return detailColumn;
  }

  private shapeGroup(
    group: MarketGroup,
  ): NormalizedMarketDashboardResponse['groups'][number] {
    delete group.items;

    return group;
  }

  private normalizeWidgetData(widget: MarketWidget): MarketWidget {
    if (widget) {
      widget.settings = JSON.parse(
        widget.settings as any,
        DateParserService.getJsonDateReviver(),
      );

      widget.dataSources.forEach((dataSource: any) => {
        dataSource.settings = JSON.parse(
          dataSource.settings as string,
          DateParserService.getJsonDateReviver(),
        );

        // TODO: Delete after successfully saved to db on customer!
        const settingsMigration = dataSource.settings as any;
        if (
          settingsMigration.sortOrder !== null &&
          settingsMigration.sortOrder !== undefined
        ) {
          dataSource.settings.identifier.sortOrder =
            settingsMigration.sortOrder;

          delete settingsMigration.sortOrder;
        }

        if (
          settingsMigration.ignoreGaps === true ||
          settingsMigration.ignoreGaps === false
        ) {
          dataSource.settings.identifier.ignoreGaps =
            settingsMigration.ignoreGaps;

          delete settingsMigration.ignoreGaps;
        }

        if (settingsMigration.calculationType) {
          dataSource.settings.identifier.calculationType =
            settingsMigration.calculationType;

          delete settingsMigration.calculationType;
        }
      });
    }

    return widget;
  }
}
